import { useEffect } from 'react';
import { matchRoutes, type RouteObject } from 'react-router';

import { getEnvVariable } from '~/dev/utils/getEnvVariable';
import { useRouting } from '~/routing/useRouting';

import { routes } from './routes';

/**
 * Publishes the route pattern the router *actually matched* (e.g.
 * `/workflow/tickets/:ticketId`) to the DOM so end-to-end tests can record
 * which routes a test exercises.
 *
 * Why this exists, and why it lives here:
 * This is the single runtime source of route identity. It derives patterns from
 * the same `routes` table the app renders from (and that the
 * `affected-routes` static analysis parses), so the string emitted here is
 * byte-for-byte the identifier the QE suite matches against. Nothing downstream
 * has to parse URLs, strip tenant prefixes, or reconstruct `:params`.
 *
 * It must be centralized because only a `matchRoutes` against the full table can
 * recover the complete nested pattern.
 *
 * Nested matches also publish `data-active-route-branch` — a JSON array of every
 * ancestor pattern in the matched branch (leaf last). Layout-component changes
 * affect the parent pattern; without the branch, QE's index would only ever see
 * the deepest leaf and miss parent-route coverage.
 *
 * It is a no-op in production: route patterns are already in the bundle so this
 * is harmless, but there is no reason to ship a test hook to real users. We gate
 * on `SENTRY_ENVIRONMENT` (the cell name, already provisioned per cell via
 * Consul), so the emission works on main/release/preview with zero new
 * deployment config and stays off on the `production*` cells.
 */

const NOT_FOUND_PATTERN = '/*';
const PRODUCTION_ENVIRONMENT_PREFIX = 'production';

const isProductionEnvironment = () =>
  String(getEnvVariable('SENTRY_ENVIRONMENT')).startsWith(
    PRODUCTION_ENVIRONMENT_PREFIX,
  );

/**
 * Walk a matched branch's segments into the list of canonical patterns at each
 * depth. Most nested segments are relative (`tasks`), so they extend the
 * accumulated path — identical to how `affected-routes` composes nested routes
 * (`${route}/${nested.path}`).
 *
 * A route object's `path` can also be declared absolute even while nested
 * (e.g. `RoutePathType.PlaybookDetails` = `/playbooks/:playbookId`, a sibling
 * of an index child under the `/playbooks` parent). react-router keeps that
 * child's own absolute path unchanged in the matched branch, so naively
 * joining every segment double-counted the parent. An absolute segment must
 * instead replace everything accumulated before it — the same rule
 * `affected-routes`' `joinRoutePaths` already applies on the static side.
 *
 * Empty / undefined segments (index routes) are skipped so they do not
 * duplicate the parent pattern.
 */
const buildMatchedBranch = (segments: Array<string | undefined>): string[] => {
  const branch: string[] = [];
  let accumulated = '';

  for (const segment of segments) {
    if (!segment) continue;

    if (segment.startsWith('/')) {
      accumulated = segment;
    } else if (accumulated) {
      accumulated = `${accumulated}/${segment}`;
    } else {
      accumulated = segment;
    }

    const normalized = accumulated.startsWith('/')
      ? accumulated
      : `/${accumulated}`;

    branch.push(normalized);
  }

  return branch;
};

/**
 * Resolve every ancestor pattern for a pathname (leaf last), or `[]` when
 * nothing matches or only the catch-all (404) route matches. Pure, so it can
 * be unit-tested without a router or the DOM.
 */
export const resolveActiveRouteBranch = (
  routeObjects: RouteObject[],
  pathname: string,
): string[] => {
  const matches = matchRoutes(routeObjects, pathname);

  if (!matches?.length) return [];

  const branch = buildMatchedBranch(matches.map((match) => match.route.path));

  if (branch.length === 1 && branch[0] === NOT_FOUND_PATTERN) return [];

  return branch.filter((pattern) => pattern !== NOT_FOUND_PATTERN);
};

/**
 * Resolve the canonical leaf pattern for a pathname, or `null` when nothing
 * matches or only the catch-all (404) route matches.
 */
export const resolveActiveRoutePattern = (
  routeObjects: RouteObject[],
  pathname: string,
): string | null => {
  const branch = resolveActiveRouteBranch(routeObjects, pathname);

  return branch.length > 0 ? (branch[branch.length - 1] ?? null) : null;
};

export const useEmitActiveRoute = () => {
  const { location } = useRouting();

  useEffect(() => {
    if (isProductionEnvironment()) return;

    const branch = resolveActiveRouteBranch(routes, location.pathname);
    const pattern = branch.length > 0 ? branch[branch.length - 1] : null;

    if (pattern) {
      document.documentElement.dataset.activeRoute = pattern;
      document.documentElement.dataset.activeRouteBranch =
        JSON.stringify(branch);
    } else {
      delete document.documentElement.dataset.activeRoute;
      delete document.documentElement.dataset.activeRouteBranch;
    }
  }, [location.pathname]);
};
